import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import TranscriptionEdit from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/TranscriptionEdit';
import DocumentGroupEditor from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentGroupEditor';
import useOrganizationWorkflows from '@UI/Pages/Administration/DocumentGroups/Shared/Hooks/useOrganizationWorkflows';
import useWorkflowCatalog from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Hooks/useWorkflowCatalog';
import OrganizationPending from '@UI/Pages/Administration/DocumentGroups/Shared/OrganizationPending';
import { css } from '@emotion/react';
import { SearchX } from 'lucide-react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Skeleton } from '@verclam/ui-kit';

const skeletonStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

/**
 * Resolves what the editor needs before mounting it: the workflows (to find the workflow addressed by
 * the route — an automatic transcription opens its own screen), the account's document types (to label and pick documents) and the preset catalogue
 * (to start new documents from). A failed catalogue does not block: documents then start neutral.
 * No `workflowKey` in the route means a new group.
 */
const DocumentGroupEditContent: React.FC = () => {
	const navigate = useNavigate();
	const { workflowKey } = useParams();
	const { workflows, error, refetch, organizationId, hasNoOrganization } = useOrganizationWorkflows();
	const {
		documentTypes,
		error: documentTypesError,
		refetch: refetchDocumentTypes,
	} = useAccountDocumentTypes();
	const { catalog, error: catalogError } = useWorkflowCatalog();

	const handleBackClick = React.useCallback(() => {
		navigate('/administration/document-groups');
	}, [navigate]);

	const workflow = React.useMemo(() => {
		if (workflowKey === undefined || workflows === null) {
			return null;
		}

		return workflows.findByKey(workflowKey);
	}, [workflowKey, workflows]);

	if (organizationId === null) {
		return <OrganizationPending hasNoOrganization={hasNoOrganization} />;
	}

	if (workflows === null && error === null) {
		return (
			<div css={skeletonStyle}>
				<Skeleton height="6rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="12rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="12rem" borderRadius={theme.radii.xlarge} animation="wave" />
			</div>
		);
	}

	if (workflows === null) {
		return <ErrorState title="Impossible de charger le groupe de documents." onRetry={refetch} />;
	}

	if (workflow?.isAutomatic) {
		return <TranscriptionEdit workflow={workflow} organizationId={organizationId} />;
	}

	if (documentTypes === null && documentTypesError === null) {
		return (
			<div css={skeletonStyle}>
				<Skeleton height="12rem" borderRadius={theme.radii.xlarge} animation="wave" />
			</div>
		);
	}

	if (documentTypes === null) {
		return (
			<ErrorState title="Impossible de charger les types de document." onRetry={refetchDocumentTypes} />
		);
	}

	if (catalog === null && catalogError === null) {
		return (
			<div css={skeletonStyle}>
				<Skeleton height="12rem" borderRadius={theme.radii.xlarge} animation="wave" />
			</div>
		);
	}

	if (workflowKey !== undefined && workflow === null) {
		return (
			<EmptyState
				title="Groupe introuvable"
				description="Ce groupe de documents n’existe pas, ou n’appartient pas à la structure courante."
				icon={<SearchX size={28} />}
				action={<Button type="button" label="Retour aux groupes" onClick={handleBackClick} />}
			/>
		);
	}

	return (
		<DocumentGroupEditor
			key={workflow !== null ? `${workflow.key}@${workflow.version}` : 'new'}
			workflow={workflow}
			organizationId={organizationId}
		/>
	);
};

export default DocumentGroupEditContent;
